// "Boxed in": the robot is blocked in front AND too close on both sides to turn.
// It never reverses, so it holds still, asks for a human after alert_after_sec
// and gives up after abort_after_sec (both configurable on the robot — always
// read from the payload, never hard-coded).
//
// The portal sees it on three channels, all relayed by the cloud:
//   • nav_progress — the robot's own 0.5 s stream: state "boxed_in"/"stuck" plus
//     a live `boxed_in` timer object; the give-up tick is state "error" with
//     error_code "BOXED_IN". ACTION_STATE_CHANGE and ACTION_NAV_FAILED are folded
//     into nav_progress by the relay too: robot state "STUCK" (upper case) and
//     state "failed" with code "BOXED_IN".
//   • ACTION_NAV_STUCK — the help request, sent once per incident.
//   • ACTION_NAV_UNSTUCK — closes it (freed, cancelled, or a new goal).
// A give-up does NOT close the help request: the robot still needs a human.

export const BOXED_IN_CODE = "BOXED_IN";

// nav_progress states that mean the trip is still in progress.
export const BOXED_IN_NAV_STATES = new Set(["boxed_in", "stuck"]);

export const isBoxedInNavState = (state) => BOXED_IN_NAV_STATES.has(state);

export const isBoxedInFailure = (data) =>
  data?.error_code === BOXED_IN_CODE || data?.code === BOXED_IN_CODE;

// The robot streams nav_progress every 0.5 s while boxed in. A live timer that
// has gone this long without a frame (robot offline, page backgrounded) is not
// shown as a live "path blocked" any more.
export const LIVE_STALE_MS = 10000;

const toNumber = (value) => (typeof value === "number" && Number.isFinite(value) ? value : null);

/** Seconds → "m:ss". Negative and missing values read as 0:00. */
export const formatMSS = (seconds) => {
  const total = Math.max(0, Math.floor(toNumber(seconds) ?? 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

export const initialBoxedInState = {
  live: null, // latest nav_progress `boxed_in` timer, while boxed in
  helpRequest: null, // open ACTION_NAV_STUCK payload
  gaveUp: null, // { reason } once the robot has given up, until freed
  robotStuck: null, // robot state STUCK: { since, phase, seconds, receivedAt }
};

const liveFrom = (data, previous, now) => {
  const b = data?.boxed_in;
  if (!b) return previous ? { ...previous, receivedAt: now } : null;
  return {
    phase: b.phase ?? null,
    seconds: toNumber(b.seconds) ?? 0,
    alertAfter: toNumber(b.alert_after_sec),
    abortAfter: toNumber(b.abort_after_sec),
    paused: Boolean(b.paused),
    receivedAt: now,
  };
};

// The relay forwards ACTION_STATE_CHANGE as nav_progress, so robot-level states
// arrive upper-case; trip states from the robot's own stream are lower-case.
const isRobotStateFrame = (state) =>
  typeof state === "string" && state !== "" && state === state.toUpperCase();

const onRobotState = (state, data, now) => {
  if (data.state !== "STUCK") {
    // Robot is no longer stuck: close everything, in case UNSTUCK was missed.
    return { ...state, robotStuck: null, helpRequest: null, gaveUp: null };
  }
  const phase = data.boxed_in?.phase ?? null;
  return {
    ...state,
    robotStuck: {
      since: toNumber(data.boxed_in?.since),
      phase,
      seconds: toNumber(data.boxed_in?.seconds),
      receivedAt: now,
    },
    gaveUp: phase === "ABORTED" ? (state.gaveUp ?? { reason: null }) : state.gaveUp,
  };
};

const onNavProgress = (state, data, now) => {
  if (isRobotStateFrame(data.state)) return onRobotState(state, data, now);
  if (isBoxedInNavState(data.state)) {
    return { ...state, live: liveFrom(data, state.live, now) };
  }
  if (isBoxedInFailure(data)) {
    return {
      ...state,
      live: null,
      gaveUp: { reason: data.reason ?? data.error ?? state.gaveUp?.reason ?? null },
    };
  }
  return state.live ? { ...state, live: null } : state;
};

/**
 * Pure reducer. Actions: { type: "nav_progress" | "stuck" | "unstuck" | "reset",
 * data, now } — `now` is epoch ms, injected so the reducer stays testable.
 */
export const boxedInReducer = (state, action) => {
  const data = action.data ?? {};
  switch (action.type) {
    case "nav_progress":
      return onNavProgress(state, data, action.now);
    case "stuck":
      return { ...state, helpRequest: { ...data, receivedAt: action.now } };
    case "unstuck":
      return { ...state, helpRequest: null, gaveUp: null, robotStuck: null };
    case "reset":
      return initialBoxedInState;
    default:
      return state;
  }
};

// Clamped: the caller's clock can lag the frame that was just received.
const elapsedSec = (now, since) => Math.max(0, now - since) / 1000;

/** Seconds boxed in at this spot, extrapolated between frames. */
const secondsBoxedIn = (state, now) => {
  const { live, helpRequest } = state;
  if (live) return live.paused ? live.seconds : live.seconds + elapsedSec(now, live.receivedAt);
  // A help request pulled from the relay's cache is of unknown age, so it
  // cannot anchor a countdown; live frames will, if the robot is still on a goal.
  const reqSeconds = helpRequest?.pulled ? null : toNumber(helpRequest?.seconds);
  if (reqSeconds == null) return null;
  return reqSeconds + elapsedSec(now, helpRequest.receivedAt);
};

/**
 * Local epoch ms the robot was first boxed in at this spot, or null — for a
 * "stuck for m:ss" readout. Anchored on the robot's own `seconds` rather than its
 * `since` epoch, so a robot clock that drifts from the browser's cannot skew it.
 */
export const boxedInSince = (state, now) => {
  let seconds = secondsBoxedIn(state, now);
  const { robotStuck } = state;
  if (seconds == null && robotStuck?.seconds != null) {
    seconds = robotStuck.seconds + elapsedSec(now, robotStuck.receivedAt);
  }
  return seconds == null ? null : now - seconds * 1000;
};

const giveUpThreshold = (state) =>
  state.live?.abortAfter ?? toNumber(state.helpRequest?.abort_after_sec);

const targetNameOf = (state) =>
  state.helpRequest?.target_name ?? state.helpRequest?.target?.name ?? null;

const clearanceLabel = (metres) => {
  if (metres == null) return "no reading";
  if (metres < 0) return "clear";
  return `${Math.round(metres * 100)} cm`;
};

/** Sensor details for whoever goes to help (admin portal). */
export const describeHelpDetails = (helpRequest) => {
  if (!helpRequest) return null;
  return {
    frontSource: helpRequest.front_source ?? null,
    left: clearanceLabel(toNumber(helpRequest.left_clearance_m)),
    right: clearanceLabel(toNumber(helpRequest.right_clearance_m)),
    pose: helpRequest.pose ?? null,
  };
};

const timerLabel = (state, now) => {
  if (state.live?.paused) return "Timer paused (e-stop / hold)";
  const seconds = secondsBoxedIn(state, now);
  const abortAfter = giveUpThreshold(state);
  if (seconds == null || abortAfter == null) return null;
  // Rounded up, as countdowns are: it reads 4:00 at the start, 0:01 at the end.
  return `Gives up in ${formatMSS(Math.ceil(abortAfter - seconds))}`;
};

/**
 * What to show, or null when the robot is not boxed in. `kind` is one of
 * "blocked" (holding, before help is requested), "stuck" (help requested) or
 * "gave_up" (robot gave up and still needs a human).
 */
export const describeBoxedIn = (state, now) => {
  const target = targetNameOf(state);
  if (state.gaveUp) {
    return {
      kind: "gave_up",
      tone: "danger",
      title: "Couldn't get through — needs manual help",
      detail: "Blocked in front and on both sides. Clear the way around the robot.",
      timer: null,
      target,
    };
  }
  const helpRequested =
    Boolean(state.helpRequest) || state.live?.phase === "STUCK" || Boolean(state.robotStuck);
  if (helpRequested) {
    return {
      kind: "stuck",
      tone: "danger",
      title: "Robot is stuck — clear the way around it",
      detail: "Blocked in front and on both sides. It carries on by itself once there is room.",
      timer: timerLabel(state, now),
      target,
    };
  }
  if (state.live && now - state.live.receivedAt <= LIVE_STALE_MS) {
    const seconds = secondsBoxedIn(state, now);
    return {
      kind: "blocked",
      tone: "warning",
      title: "Path blocked — waiting for space",
      detail: null,
      timer: state.live.paused ? "Timer paused (e-stop / hold)" : formatMSS(seconds),
      target,
    };
  }
  return null;
};

/** True while there is anything to show, so callers can tick a clock. */
export const isBoxedInActive = (state) =>
  Boolean(state.live || state.helpRequest || state.gaveUp || state.robotStuck);

// ── Fleet view: open help requests across several robots ───────────────────
// Only robots that asked for help (or report state STUCK) get an entry; the
// pre-alert "path blocked" phase belongs to the trip view, not a fleet alert.

export const initialFleetState = { bySerial: {}, dismissed: {} };

const opensEntry = (action) =>
  action.type === "stuck" || (action.type === "nav_progress" && action.data?.state === "STUCK");

const withoutKey = (map, key) => Object.fromEntries(Object.entries(map).filter(([k]) => k !== key));

/** Pure reducer; actions are boxedInReducer's plus `serial`. */
export const fleetBoxedInReducer = (state, action) => {
  const serial = action.serial == null ? null : String(action.serial);
  if (serial == null) return state;
  if (action.type === "dismiss") {
    return { ...state, dismissed: { ...state.dismissed, [serial]: true } };
  }
  const prev = state.bySerial[serial];
  if (!prev && !opensEntry(action)) return state;

  const next = boxedInReducer(prev ?? initialBoxedInState, action);
  const kind = describeBoxedIn(next, action.now ?? 0)?.kind;
  if (kind !== "stuck" && kind !== "gave_up") {
    return {
      bySerial: withoutKey(state.bySerial, serial),
      dismissed: withoutKey(state.dismissed, serial),
    };
  }
  // A fresh, paging help request re-shows an alert the user had dismissed.
  const repage = action.type === "stuck" && action.data?.notify !== false;
  return {
    bySerial: { ...state.bySerial, [serial]: next },
    dismissed: repage ? withoutKey(state.dismissed, serial) : state.dismissed,
  };
};
